import { ProductData } from '@appcharge/shared-ui';
import {
  OfferData,
  ResponseTabData,
  TimeFrameObject,
} from '../../constants/apiResponses.types';
import { EOfferType, EPopupSubType } from '../../constants/enums';

interface PartitionedOffers {
  offers: OfferData[];
  progressBars: OfferData[];
}

export const partitionOffersByType = (events: OfferData[]): PartitionedOffers => {
  const offers: OfferData[] = [];
  const progressBars: OfferData[] = [];
  for (const event of events) {
    if (event.offerType === EOfferType.PROGRESS_BAR) {
      progressBars.push(event);
    } else {
      offers.push(event);
    }
  }
  return { offers, progressBars };
};

export const computeBadgeInd = (offer: OfferData): boolean => {
  const subType = offer.subType;
  const hasBadgeImage = Boolean(offer.offerUi?.badgeCoverImage);
  const isBadgeSubType =
    subType === EPopupSubType.DAILY_BONUS ||
    subType === EPopupSubType.STORE_REFRESH;
  return hasBadgeImage && isBadgeSubType;
};

export const computeTimeLeftSec = (offer: OfferData): number | null => {
  const endMs = getActiveTimeFrameEndsAtMs(offer.schedule);
  if (endMs == null) return null;
  const remainingMs = endMs - Date.now();
  if (!Number.isFinite(remainingMs)) return null;
  return Math.max(0, Math.floor(remainingMs / 1000));
};

export const arrangeMixpanelData = (data: OfferData[], tabs?: ResponseTabData[]) => {
  return data.map((offer: OfferData) => ({
    offer_id: offer.offerId,
    external_id: offer.publisherOfferId,
    name: offer.offerName,
    tab: tabs?.find((tab: ResponseTabData) => tab.tabId === offer.offerTabId)?.name,
    sale_percentage: offer.productSale?.sale,
    type: offer.offerType,
    index_to_collect: offer?.indexToCollect,
    sub_type: offer?.subType,
    start_date: offer.schedule?.timeFrames[0]?.startTime,
    end_date: offer.schedule?.timeFrames[0]?.endTime,
    products: offer.productsSequence[
      offer.indexToCollect ? offer.indexToCollect - 1 : 0
    ].products.map((product: ProductData) => ({
      product_id: product.publisherProductId,
      product_name: product.name,
      product_type: product.productType,
      product_quantity: product.quantity,
    })),
    priceInUsdCents:
      offer.productsSequence[offer.indexToCollect ? offer.indexToCollect - 1 : 0]?.prices
        ?.priceInUsdCents || 0,
    trigger_name: null,
    trigger_frequency: offer.displayRule?.frequency ?? null,
    time_left: computeTimeLeftSec(offer),
    badge_ind: computeBadgeInd(offer),
  }));
};

const getActiveTimeFrameEndsAtMs = (
  schedule: OfferData['schedule'] | undefined,
): number | null => {
  if (!schedule || schedule.permanent) return null;

  const timeFrames = schedule.timeFrames as TimeFrameObject[] | undefined;
  if (!timeFrames?.length) return null;

  const now = Date.now();
  for (const frame of timeFrames) {
    const start = new Date(frame.startTime).getTime();
    const end = new Date(frame.endTime).getTime();
    if (
      Number.isFinite(start) &&
      Number.isFinite(end) &&
      start <= now &&
      now <= end
    ) {
      return end;
    }
  }
  return null;
};
